import { useState, useRef, useEffect } from 'react';
import { Camera, Upload, X } from 'lucide-react';
import { useUploadFile, useDeleteFile } from '@/hooks/files';
import { filesApi, type UploadedFile, type FileCategory, type OwnerType, type Visibility } from '@/api/http-client';
import { ImageCropAdjustModal } from './ImageCropAdjustModal';

export interface AvatarUploaderProps {
  ownerId: string;
  ownerType: OwnerType;
  currentUrl?: string | null;
  onUploadSuccess?: (file: UploadedFile) => void;
  onDeleteSuccess?: () => void;
  category?: FileCategory;
  visibility?: Visibility;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

const SIZE_CLASSES = {
  sm: 'h-16 w-16',
  md: 'h-24 w-24',
  lg: 'h-32 w-32',
  xl: 'h-40 w-40',
};

export function AvatarUploader({
  ownerId,
  ownerType,
  currentUrl,
  onUploadSuccess,
  onDeleteSuccess,
  category = 'PROFILE_PHOTO',
  visibility = 'PUBLIC',
  size = 'lg',
  className = '',
}: AvatarUploaderProps) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentUrl || null);
  const [fileId, setFileId] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [isCropOpen, setIsCropOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setPreviewUrl(currentUrl || null);
  }, [currentUrl]);

  const { upload } = useUploadFile({
    ownerId,
    ownerType,
    category,
    visibility,
    onSuccess: (file) => {
      setIsUploading(false);
      setFileId(file.id);
      const url = file.url || (file.key ? filesApi.getFileUrl(file.key) : null);
      setPreviewUrl(url);
      onUploadSuccess?.(file);
    },
    onError: (err) => {
      setIsUploading(false);
      setError(err.message);
    },
  });

  const { deleteFile, isDeleting } = useDeleteFile({
    ownerId,
    onSuccess: () => {
      setPreviewUrl(null);
      setFileId(null);
      onDeleteSuccess?.();
    },
  });

  const handleFileSelect = async (files: File[]) => {
    const file = files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      setCropSrc(reader.result as string);
      setIsCropOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const handleApplyCropped = async (croppedBase64: string) => {
    setIsUploading(true);
    setError(null);
    setPreviewUrl(croppedBase64);

    try {
      // Converte o base64 recortado em File para upload
      const res = await fetch(croppedBase64);
      const blob = await res.blob();
      const croppedFile = new File([blob], 'avatar_cropped.jpg', { type: 'image/jpeg' });
      await upload(croppedFile);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
      setIsUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!previewUrl && !fileId) return;
    try {
      const targetId = fileId || previewUrl || '';
      await deleteFile(targetId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className={`relative inline-block ${className}`}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={(e) => {
          const files = e.target.files;
          if (files) {
            handleFileSelect(Array.from(files));
          }
          e.target.value = '';
        }}
        className="hidden"
      />

      <div
        className={`
          ${SIZE_CLASSES[size]}
          rounded-full bg-muted overflow-hidden cursor-pointer
          group relative
          ${isUploading || isDeleting ? 'opacity-50' : ''}
        `}
        onClick={handleClick}
      >
        {previewUrl ? (
          <img
            src={filesApi.getFileUrl(previewUrl)}
            alt="Avatar"
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="h-full w-full flex items-center justify-center">
            <Camera className="h-8 w-8 text-muted-foreground" />
          </div>
        )}

        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
          {isUploading ? (
            <span className="text-white text-sm">Enviando...</span>
          ) : (
            <>
              <Upload className="h-6 w-6 text-white" />
              <span className="text-white text-sm">Alterar</span>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="absolute -bottom-6 left-0 right-0 text-center">
          <span className="text-xs text-red-500">{error}</span>
        </div>
      )}

      {previewUrl && !isUploading && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleDelete();
          }}
          className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600 transition-colors"
          disabled={isDeleting}
        >
          <X className="h-3 w-3" />
        </button>
      )}

      {cropSrc && (
        <ImageCropAdjustModal
          imageSrc={cropSrc}
          isOpen={isCropOpen}
          onClose={() => setIsCropOpen(false)}
          onApplyCrop={handleApplyCropped}
          aspectRatio={1}
          title="Ajustar e Enquadrar Foto de Perfil"
          isCircle={true}
        />
      )}
    </div>
  );
}
